import {
    ICalendarCreateEventBodyAttendeeType,
    ICalendarCreateEventBodyContentType,
    ICalendarCreateEventBodyEntity,
    ICalendarCreateEventBodyTimeZone,
    ICalendarItemEntity,
    IClientEntity,
    IPlanningItemEntity,
    PlanningItemType,
} from '@phrospr/lib-models';
import { MailEngine } from './mail.engine';
import { PlanningItemEngine } from './planning-item.engine';

export function newCalendarItemEntity(): ICalendarItemEntity {
    return {
        calendar_item_id: null,
        planning_item_id: null,
        date_time_start: null,
        date_time_stop: null,
        created_by_user_id: null,
        in_calendar_of_user_id: null,
        data: null,
        appointment_type: null,
        // temporary until we remove this field
        active: false,
    };
}

export namespace CalendarItemEngine {
    const titlePrefixMapper = {
        [PlanningItemType.maintenance]: 'Ond. - ',
        [PlanningItemType.lead]: 'Lead. - ',
        [PlanningItemType.delivery]: 'Lev. - ',
        [PlanningItemType.repair]: 'Dep. - ',
        [PlanningItemType.placement]: 'Pla. - ',
    };

    export function getTitleForCalendarEvent(
        planning_item_type: string,
        titlePrefix: boolean,
        client?: IClientEntity,
    ): string {
        return `${
            titlePrefix
                ? titlePrefixMapper[planning_item_type]
                    ? titlePrefixMapper[planning_item_type]
                    : '??? - '
                : ''
        }${PlanningItemEngine.getClientName(client)}`;
    }

    // Portal detail page of a planning item (Minimal app route). `origin` is the tenant's public
    // portal URL — the backend passes confGlobal.apiUrl; there is no safe default across tenants.
    export function getPlanningItemPath(planning_item_id: number): string {
        return `/dashboard/planning-items/${planning_item_id}`;
    }

    export function getPlanningItemUrl(planning_item_id: number, origin: string): string {
        return `${origin.replace(/\/$/, '')}${getPlanningItemPath(planning_item_id)}`;
    }

    export function getPlanningItemLink(planning_item_id: number, origin: string): string {
        const link = getPlanningItemUrl(planning_item_id, origin);
        return `<br/><a href="${link}">
${link}</a><br/>`;
    }

    // Recognizes the portal link embedded in an Outlook event body; the trailing guard keeps
    // 42 from matching 420.
    export function bodyReferencesPlanningItem(content: string | null | undefined, planning_item_id: number): boolean {
        if (!content) return false;
        return new RegExp(`/planning-items/${planning_item_id}(?!\\d)`).test(content);
    }

    // Same "{zip} {city} {street}" format the portal plan dialog prefills, so
    // Movetex-planned events get the identical Outlook location.
    export function getPlanningItemLocation(planningItem: IPlanningItemEntity): string {
        const address = planningItem?.data?.delivery_address;
        if (!address) return '';
        return `${address.zip_code ?? ''} ${address.city ?? ''} ${address.address ?? ''}`.trim();
    }

    const escapeHtml = (text: string): string =>
        text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    export function textToHtml(text: string): string {
        return `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`;
    }

    // Single source of truth for a generated event body: also used to re-render the
    // body when planning item data (e.g. the description) changes after planning.
    export function generateEventBodyContent(
        planningItem: IPlanningItemEntity,
        client: IClientEntity | undefined,
        origin: string,
    ): string {
        return (
            textToHtml(MailEngine.getPlanningMailContent(planningItem, client)) +
            getPlanningItemLink(planningItem.planning_item_id, origin)
        );
    }

    export function generateCalendarCreateBody(
        data: {
            location: string;
            email: string;
            additional_attendees?: string[];
            start_time: Date;
            stop_time: Date;
            recurringDays?: number;
        },
        planningItem: IPlanningItemEntity,
        titlePrefix: boolean,
        client: IClientEntity | undefined,
        content: string | undefined,
        origin: string,
    ): ICalendarCreateEventBodyEntity {
        return {
            subject: getTitleForCalendarEvent(planningItem.planning_item_type, titlePrefix, client),
            start: {
                datetime: data.start_time.toISOString(),
                timeZone: ICalendarCreateEventBodyTimeZone.UTC,
            },
            end: {
                datetime: data.stop_time.toISOString(),
                timeZone: ICalendarCreateEventBodyTimeZone.UTC,
            },
            location: {
                displayName: data.location,
            },
            // first email = main person; the sync consumer creates the event in their mailbox
            // and keeps the remaining addresses as real Outlook attendees
            attendees: [data.email, ...(data.additional_attendees ?? [])].map(address => ({
                emailAddress: { address, name: null },
                type: ICalendarCreateEventBodyAttendeeType.Required,
            })),
            body: {
                contentType: ICalendarCreateEventBodyContentType.HTML,
                // The generated template is plain text but the event body is sent as HTML —
                // without the conversion Outlook collapses every newline into one blob.
                content: content
                    ? content + getPlanningItemLink(planningItem.planning_item_id, origin)
                    : generateEventBodyContent(planningItem, client, origin),
            },
            recurringDays: data.recurringDays,
        };
    }
}
